import { Controller } from "@hotwired/stimulus"
// Admin venue page: a photo or video tile opens in an overlay with the
// verdicts that matter — hide or show, and for a photo, make it the cover.
// Each verdict posts at once and paints back on the tile, so a later Save
// of the form agrees with what the admin sees.
//
// Video: a held file plays natively, with sound, and the mute choice is
// remembered across openings (localStorage). Without a file, the
// provider's player loads in an iframe — TikTok's takes autoplay and sound
// as parameters, Instagram's insists on its own click.
//
// Arrows (and ← →) step through the open tile's own row — the photos, the
// studio's videos or the creators' — wrapping at the ends; a lone tile shows
// no arrows. The row is the tile's parent grid, nothing to declare.
export default class extends Controller {
  static targets = ["overlay", "video", "frame", "image", "toggle", "cover", "title"]
  static MUTE_KEY = "admin.media.muted"

  connect() {
    // The overlay is authored inside the media section but lives on <body>:
    // a fixed box inside a transformed or clipped ancestor stops short of the
    // viewport. Its parts are resolved once, before the move, because a
    // Stimulus target outside its controller's element is no target.
    this.overlay = this.overlayTarget
    this.video = this.videoTarget; this.frame = this.frameTarget; this.image = this.imageTarget
    this.toggleBtn = this.toggleTarget; this.coverBtn = this.coverTarget; this.title = this.titleTarget
    document.body.appendChild(this.overlay)
    // Moved out of the controller's element, the overlay's data-actions no
    // longer bind: its handlers are wired here by hand.
    this.overlay.addEventListener("click", (e) => this.backdrop(e))
    this.toggleBtn.addEventListener("click", () => this.toggle())
    this.coverBtn.addEventListener("click", () => this.makeCover())
    this.overlay.querySelector("[data-media-close]")?.addEventListener("click", () => this.close())
    this.prevBtn = this.overlay.querySelector("[data-media-prev]")
    this.nextBtn = this.overlay.querySelector("[data-media-next]")
    this.prevBtn?.addEventListener("click", () => this.step(-1))
    this.nextBtn?.addEventListener("click", () => this.step(1))
    this._onKey = (e) => {
      if (this.overlay.classList.contains("hidden")) return
      if (e.key === "Escape") this.close()
      if (e.key === "ArrowLeft") this.step(-1)
      if (e.key === "ArrowRight") this.step(1)
    }
    document.addEventListener("keydown", this._onKey)
    // The strips under the tiles save on change too — no Save needed.
    this.element.addEventListener("change", (e) => this.stripChanged(e))
    this.video.addEventListener("volumechange", () => {
      localStorage.setItem(this.constructor.MUTE_KEY, this.video.muted ? "1" : "0")
    })
    // The file's own shape, the moment it is known — the tile's thumbnail was
    // only the opening guess.
    this.video.addEventListener("loadedmetadata", () => {
      this.shape(this.video.videoWidth, this.video.videoHeight)
    })
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
    this.unlockScroll()
    this.overlay?.remove()
  }

  open(event) {
    if (event.target.closest("label, input")) return // the strip's own controls keep their click
    this.openTile(event.currentTarget)
    this.overlay.classList.remove("hidden")
    this.lockScroll()
  }

  openTile(tile) {
    this.tile = tile
    this.toggleUrl = tile.dataset.toggleUrl
    this.coverUrl = tile.dataset.coverUrl
    this.title.textContent = tile.dataset.caption || ""
    this.show(tile.dataset)
    this.paint(tile.dataset.hidden === "true", tile.dataset.cover === "true")
    // A picture the page would never show (too small, or the sweep's
    // thumbnail) gets neither verdict button — the tile already says why.
    const unfit = tile.dataset.unfit === "true"
    this.toggleBtn.classList.toggle("hidden", unfit)
    if (unfit) this.coverBtn.classList.add("hidden")
    const alone = this.siblings().length < 2
    this.prevBtn?.classList.toggle("hidden", alone)
    this.nextBtn?.classList.toggle("hidden", alone)
  }

  // The tiles of the open one's row, in page order.
  siblings() {
    return [...this.tile.parentElement.querySelectorAll(':scope > [data-action="click->media-modal#open"]')]
  }

  step(dir) {
    const tiles = this.siblings()
    if (tiles.length < 2) return
    const i = tiles.indexOf(this.tile)
    this.openTile(tiles[(i + dir + tiles.length) % tiles.length])
  }

  // The page behind must not scroll while the modal is up.
  lockScroll() { document.documentElement.style.overflow = "hidden" }
  unlockScroll() { document.documentElement.style.overflow = "" }

  show({ mediaUrl, embedUrl, imageUrl }) {
    this.video.classList.toggle("hidden", !mediaUrl)
    this.frame.classList.toggle("hidden", !!mediaUrl || !embedUrl)
    this.image.classList.toggle("hidden", !imageUrl)
    this.coverBtn.classList.toggle("hidden", !imageUrl)
    if (mediaUrl) {
      // The tile's thumbnail is already loaded and carries the video's shape:
      // sizing the box now means the modal never collapses to nothing while
      // the next file loads, and never letterboxes once it has.
      const thumb = this.tile?.querySelector("img")
      this.shape(thumb?.naturalWidth, thumb?.naturalHeight)
      this.video.muted = localStorage.getItem(this.constructor.MUTE_KEY) === "1"
      this.video.src = mediaUrl
      // With sound, a browser may refuse to start: play muted rather than
      // leave the file on its poster where an embed used to autoplay.
      this.video.play().catch(() => {
        this.video.muted = true
        this.video.play().catch(() => {})
      })
    } else {
      this.video.pause()
      this.video.removeAttribute("src")
    }
    this.frame.src = !mediaUrl && embedUrl ? embedUrl : "about:blank"
    if (imageUrl) this.image.src = imageUrl
  }

  // Hold the media box at a known ratio; a source that will not say keeps
  // whatever the last one set, which is closer than collapsing.
  shape(width, height) {
    if (width > 0 && height > 0) this.video.style.aspectRatio = `${width} / ${height}`
  }

  close() {
    this.overlay.classList.add("hidden")
    this.unlockScroll()
    this.video.pause()
    this.video.removeAttribute("src")
    this.frame.src = "about:blank"
  }

  backdrop(event) { if (event.target === this.overlay) this.close() }

  async toggle() {
    const { hidden } = await this.post(this.toggleUrl)
    this.tile.dataset.hidden = String(hidden)
    const box = this.tile.querySelector("input[type=checkbox]")
    // The tile's checkbox says "show", the JSON says "hidden": negate.
    if (box) box.checked = !hidden
    this.tile.querySelector("img")?.classList.toggle("opacity-30", hidden)
    this.paint(hidden, this.tile.dataset.cover === "true")
  }

  async makeCover() {
    await this.post(this.coverUrl)
    this.element.querySelectorAll("[data-cover]").forEach((t) => {
      const on = t === this.tile
      t.dataset.cover = String(on)
      t.classList.toggle("ring-2", on); t.classList.toggle("ring-accent", on); t.classList.toggle("border-transparent", on)
      const radio = t.querySelector("input[type=radio]")
      if (radio) radio.checked = on
    })
    this.paint(this.tile.dataset.hidden === "true", true)
  }

  // A tile's own show box or cover radio, saved the moment it changes. No Save
  // button is involved: the box posts to toggle_photo, which writes at once.
  async stripChanged(event) {
    const input = event.target
    const tile = input.closest("[data-toggle-url]")
    if (!tile) return
    if (input.type === "checkbox") {
      // The box says "show", the JSON says "hidden": negate, both ways. Writing
      // `input.checked = hidden` here unticks the box the admin just ticked —
      // the write had gone through, so only a reload showed the truth.
      const { hidden } = await this.post(`${tile.dataset.toggleUrl}&hidden=${input.checked ? 0 : 1}`)
      tile.dataset.hidden = String(hidden)
      input.checked = !hidden
      tile.querySelector("img")?.classList.toggle("opacity-30", hidden)
    } else if (input.type === "radio" && input.checked && tile.dataset.coverUrl) {
      // Both, and not `this.tile` alone: makeCover posts `this.coverUrl`, which
      // only openTile ever set. Electing a cover from the grid without having
      // opened the modal first posted to `undefined`, took a 404, and threw —
      // no ring, and nothing written.
      this.tile = tile
      this.coverUrl = tile.dataset.coverUrl
      await this.makeCover()
    }
  }

  async post(url) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content, "Accept": "application/json" }
    })
    if (!res.ok) throw new Error(res.status)
    return res.json()
  }

  paint(hidden, cover) {
    this.toggleBtn.textContent = hidden ? "Show on the page" : "Hide from the page"
    this.toggleBtn.classList.toggle("btn-dark", hidden)
    this.toggleBtn.classList.toggle("btn-secondary", !hidden)
    this.coverBtn.textContent = cover ? "Cover" : "Make cover"
    this.coverBtn.disabled = cover
  }
}
